import React, { useEffect, useMemo } from 'react';
import {
  Header,
  ContentLayout,
  Table,
  SpaceBetween,
  Box,
  TableProps,
  Container,
  PropertyFilter,
} from '@cloudscape-design/components';
import { Capability } from '@amzn/finspace-service-typescript-client';
import { Button } from '../common/Button';
import { TrajectoryLoader } from '../common/Loaders';
import { useCapabilitiesStore } from '../../store/capability-store';
import { useTableSorting, createStandardComparator, usePropertyFilter } from '../../hooks';
import { PROPERTY_FILTER_I18N_STRINGS } from '../../constants/cloudscape-i18n';
import { ROUTES } from '../../constants';
import './capabilities-list-page.scss';

const CapabilitiesListPage: React.FC = () => {
  const { capabilities, isLoading, error, fetchCapabilities } = useCapabilitiesStore();

  // Fetch capabilities on component mount
  useEffect(() => {
    fetchCapabilities();
  }, [fetchCapabilities]);

  // Property filter for name and description
  const {
    query: filteringQuery,
    setQuery: setFilteringQuery,
    filteredItems: filteredCapabilities,
    filteringProperties,
    filteringOptions,
  } = usePropertyFilter({
    items: capabilities?.capabilities || [],
    filterConfigs: [
      {
        key: 'name',
        propertyLabel: 'Name',
        groupValuesLabel: 'Name values',
        getValue: (capability: Capability) => capability.name,
      },
      {
        key: 'description',
        propertyLabel: 'Description',
        groupValuesLabel: 'Description values',
        getValue: (capability: Capability) => capability.description,
      },
    ],
  });

  // Setup sorting
  const sortingComparator = useMemo(
    () => createStandardComparator<Capability>({
      name: (item) => item.name?.toLowerCase() || "",
      description: (item) => item.description?.toLowerCase() || "",
    }),
    []
  );

  const {
    sortedItems,
    sortingColumn,
    sortingDescending,
    handleSortingChange,
  } = useTableSorting({
    items: filteredCapabilities,
    defaultSortingField: "name",
    defaultSortingDescending: false,
    sortingComparator,
  });

  if (isLoading) {
    return (
      <ContentLayout defaultPadding>
        <div className="loading-state">
          <TrajectoryLoader size="140px" speed="2s" />
          <div className="loading-message">
            Loading capabilities...
          </div>
        </div>
      </ContentLayout>
    );
  }

  if (error) {
    return (
      <ContentLayout defaultPadding>
        <Box textAlign="center">
          Error loading capabilities: {error.message}
        </Box>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout
      defaultPadding
      header={<Header variant="h1">Capabilities</Header>}
    >
      <SpaceBetween direction="vertical" size="l">
        <Container
          header={
            <PropertyFilter
              query={filteringQuery}
              onChange={({ detail }) => setFilteringQuery(detail)}
              filteringProperties={filteringProperties}
              filteringOptions={filteringOptions}
              countText={`${sortedItems.length} ${sortedItems.length === 1 ? 'match' : 'matches'}`}
              i18nStrings={{
                ...PROPERTY_FILTER_I18N_STRINGS,
                filteringPlaceholder: "Search by capability name or description",
              }}
            />
          }
        >
          <Table
          columnDefinitions={[
            {
              id: 'capability',
              header: <Box fontWeight="bold">Name</Box>,
              cell: (item: Capability) => (
                <Box>
                  <div className="capability-name">
                    {item.name}
                  </div>
                </Box>
              ),
              sortingField: "name",
              width: "38%",
              minWidth: "200px",
            },
            {
              id: 'description',
              header: <Box fontWeight="bold">Description</Box>,
              cell: (item: Capability) => (
                <div className="capability-description">
                  {item.description || '-'}
                </div>
              ),
              sortingField: "description",
              width: "52%",
              minWidth: "300px",
            },
            {
              id: 'actions',
              header: <Box fontWeight="bold">Actions</Box>,
              cell: (item: Capability) => (
                <div className="capability-actions">
                  <Button
                    variant="secondary"
                    icon="edit"
                    href={`${ROUTES.CAPABILITIES_EDIT}/${item.id!}`}
                  >
                    Edit
                  </Button>
                </div>
              ),
              width: "10%",
              minWidth: "90px",
            }
          ]}
          items={sortedItems}
          loading={isLoading}
          loadingText="Loading capabilities..."
          sortingColumn={sortingColumn}
          sortingDescending={sortingDescending}
          onSortingChange={handleSortingChange}
          empty={
            <Box textAlign="center">
              No capabilities found
            </Box>
          }
          />
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default React.memo(CapabilitiesListPage);
